import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  useDisclosure,
  useToast,
  Text,
  VStack,
  Spinner,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  ArrowBackIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import TransactionModal from "../../components/Admin/TransactionModal";

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all, inflow, outflow
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch transactions
  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${proxy}server_mts/admin/GetAllOperations.php`);
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error) {
      toast({
        title: "Error loading transactions",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter by search and type
  useEffect(() => {
    let filtered = [...transactions];

    // Filter by type
    if (typeFilter === "inflow") {
      filtered = filtered.filter((t) => Number(t.out_in) === 1);
    } else if (typeFilter === "outflow") {
      filtered = filtered.filter((t) => Number(t.out_in) === 0);
    }

    // Filter by search
    if (searchQuery.trim() !== "") {
      filtered = filtered.filter(
        (t) =>
          t.symbol_crypto.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.from_add?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.to_add?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
  }, [searchQuery, typeFilter, transactions]);

  // Add transaction
  const handleAdd = () => {
    setSelectedTransaction(null);
    onOpen();
  };

  // Edit transaction
  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    onOpen();
  };

  // Delete transaction
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) {
      return;
    }

    try {
      await axios.post(`${proxy}server_mts/admin/DeleteOperation.php`, { id });
      toast({
        title: "Transaction deleted successfully",
        status: "success",
        duration: 2000,
        position: "top",
      });
      fetchTransactions();
    } catch (error) {
      toast({
        title: "Error deleting transaction",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  // Save transaction
  const handleSave = () => {
    onClose();
    setTimeout(() => {
      fetchTransactions();
    }, 200);
  };

  // Stats
  const totalInflow = transactions.filter((t) => Number(t.out_in) === 1).length;
  const totalOutflow = transactions.filter((t) => Number(t.out_in) === 0).length;

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading transactions...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="Back to dashboard"
              />
              <VStack align="start" spacing={0}>
                <Heading
                  size="md"
                  bgGradient="linear(to-r, teal.300, cyan.400)"
                  bgClip="text"
                >
                  💸 Transaction Management
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Manage crypto transactions (inflows & outflows)
                </Text>
              </VStack>
            </HStack>

            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={handleAdd}
              size="sm"
            >
              Add Transaction
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Search & Filters */}
          <HStack justify="space-between" spacing={4} flexWrap="wrap">
            <HStack spacing={3}>
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by symbol or address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  _hover={{ borderColor: "whiteAlpha.400" }}
                  _focus={{ borderColor: "teal.400" }}
                  color="white"
                  size="sm"
                />
              </InputGroup>

              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                size="sm"
                maxW="150px"
              >
                <option value="all" style={{ background: "#2D3748" }}>
                  All Types
                </option>
                <option value="inflow" style={{ background: "#2D3748" }}>
                  Inflows
                </option>
                <option value="outflow" style={{ background: "#2D3748" }}>
                  Outflows
                </option>
              </Select>
            </HStack>

            <HStack spacing={3}>
              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                ↑ {totalInflow} Inflows
              </Badge>
              <Badge colorScheme="red" fontSize="sm" px={3} py={1}>
                ↓ {totalOutflow} Outflows
              </Badge>
              <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
                {filteredTransactions.length} Total
              </Badge>
            </HStack>
          </HStack>

          {/* Table */}
          <Box
            bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            overflow="hidden"
            boxShadow="0 10px 30px rgba(0,0,0,0.3)"
          >
            {filteredTransactions.length === 0 ? (
              <Center py={12}>
                <VStack spacing={3}>
                  <Text fontSize="4xl">📭</Text>
                  <Text color="gray.400" fontSize="lg">
                    {searchQuery || typeFilter !== "all"
                      ? "No transactions found"
                      : "No transactions yet"}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {searchQuery || typeFilter !== "all"
                      ? "Try different filters"
                      : "Click 'Add Transaction' to get started"}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="whiteAlpha.50">
                    <Tr>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        ID
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        Date
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        Type
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        Amount
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        Asset
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        From
                      </Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">
                        To
                      </Th>
                      <Th
                        color="teal.300"
                        borderColor="whiteAlpha.200"
                        textAlign="right"
                      >
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredTransactions.map((tx) => {
                      const isIn = Number(tx.out_in) === 1;
                      return (
                        <Tr
                          key={tx.id}
                          _hover={{ bg: "whiteAlpha.50" }}
                          transition="0.2s"
                        >
                          <Td color="gray.400" borderColor="whiteAlpha.100">
                            #{tx.id}
                          </Td>
                          <Td color="gray.300" borderColor="whiteAlpha.100">
                            {new Date(tx.date_operation).toLocaleDateString()}
                          </Td>
                          <Td borderColor="whiteAlpha.100">
                            <Badge
                              colorScheme={isIn ? "green" : "red"}
                              fontSize="xs"
                            >
                              {isIn ? "↑ Inflow" : "↓ Outflow"}
                            </Badge>
                          </Td>
                          <Td
                            color={isIn ? "green.300" : "red.300"}
                            fontWeight="bold"
                            borderColor="whiteAlpha.100"
                          >
                            {isIn ? "+" : "-"}
                            {Number(tx.amount).toFixed(8)}
                          </Td>
                          <Td borderColor="whiteAlpha.100">
                            <Badge colorScheme="purple" fontSize="xs">
                              {tx.symbol_crypto}
                            </Badge>
                          </Td>
                          <Td
                            color="gray.400"
                            fontSize="xs"
                            fontFamily="monospace"
                            borderColor="whiteAlpha.100"
                          >
                            {tx.from_add
                              ? `${tx.from_add.substring(0, 8)}...`
                              : "N/A"}
                          </Td>
                          <Td
                            color="gray.400"
                            fontSize="xs"
                            fontFamily="monospace"
                            borderColor="whiteAlpha.100"
                          >
                            {tx.to_add
                              ? `${tx.to_add.substring(0, 8)}...`
                              : "N/A"}
                          </Td>
                          <Td borderColor="whiteAlpha.100" textAlign="right">
                            <HStack spacing={2} justify="flex-end">
                              <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleEdit(tx)}
                                aria-label="Edit transaction"
                              />
                              <IconButton
                                icon={<DeleteIcon />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleDelete(tx.id)}
                                aria-label="Delete transaction"
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Modal */}
      <TransactionModal
        isOpen={isOpen}
        onClose={onClose}
        transaction={selectedTransaction}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AdminTransactions;